import { useEffect, useContext, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { db } from '../../../config/firebase';
import { UserContext } from '../../../context/UserProvider';

/**
 * CurtainMonitor - Componente que monitorea las operaciones de cortinas en segundo plano
 * Este componente funciona independientemente de si el usuario está en la sección de cortinas
 * y detecta automáticamente cuando las operaciones terminan.
 */
const CurtainMonitor = forwardRef(({ onNotification, curtainsData }, ref) => {
  const { usuario, currentMac, canIdIrrigation } = useContext(UserContext);
  const [monitoredCurtains, setMonitoredCurtains] = useState([]);
  const unsubscribeRefs = useRef({});

  // Función para manejar la finalización de una operación
  const handleOperationCompletion = useCallback(async (curtain, action, completionReason) => {
    const actionText = action === 0 ? "abrir" : "cerrar";
    const actionName = action === 0 ? "Abrir" : "Cerrar";
    
    console.log(`[CurtainMonitor] Operación de ${actionText} completada para ${curtain.name}. Razón: ${completionReason}`);

    // Remover cortina de la lista de monitoreadas
    setMonitoredCurtains(prev => prev.filter(c => c.id !== curtain.id));

    // Limpiar listener si existe
    if (unsubscribeRefs.current[`firebase_${curtain.id}`]) {
      unsubscribeRefs.current[`firebase_${curtain.id}`]();
      delete unsubscribeRefs.current[`firebase_${curtain.id}`];
    }

    // Emitir evento personalizado para comunicación con otros componentes
    const event = new CustomEvent('curtainOperationComplete', {
      detail: {
        curtainId: curtain.id,
        curtainName: curtain.name,
        action: action,
        completionReason: completionReason,
        completionTime: new Date().toISOString()
      }
    });
    window.dispatchEvent(event);

    // Enviar notificación si se proporciona la función
    if (onNotification) {
      let message = "";
      switch (completionReason) {
        case "routine_completed":
          message = `La acción de ${actionText} en ${curtain.name} se completó exitosamente.`;
          break;
        case "already_in_position":
          message = `No se puede ejecutar la acción de ${actionText} en ${curtain.name}. La cortina ya se encuentra en la posición deseada.`;
          break;
        default:
          message = `La operación de ${actionText} en ${curtain.name} ha sido completada.`;
      }

      onNotification({
        type: completionReason === "already_in_position" ? "warning" : "success",
        title: completionReason === "already_in_position" ? "Acción no ejecutada" : "Operación completada",
        message: message,
        curtainId: curtain.id,
        curtainName: curtain.name,
        action: action
      });
    }
  }, [onNotification]);

  // Función para monitorear confirmaciones MQTT de Firebase para una cortina
  const monitorFirebaseConfirmations = useCallback((curtain, action) => {
    const docPath = `${usuario.username}/infoDevices/${currentMac}/${canIdIrrigation}/fromModule/configOK`;
    const docRef = db.doc(docPath);

    const unsubscribe = docRef.onSnapshot(async (snapshot) => {
      if (snapshot.exists) {
        const data = snapshot.data();

        if (data) {
          // Verificar parámetros específicos para término de rutina (val === 0)
          if (
            data.accion === 243 &&
            data.act === "recOK" &&
            data.kind === action &&
            data.outid === curtain.id &&
            data.val === 0
          ) {
            console.log(`[CurtainMonitor] Rutina completada detectada para cortina ${curtain.name}`);
            handleOperationCompletion(curtain, action, "routine_completed");
            
            // Limpiar el documento después de procesar
            try {
              const cleanData = {
                ...data,
                accion: 0,
                act: "-"
              };
              await docRef.update(cleanData);
            } catch (error) {
              console.error(`[CurtainMonitor] Error al limpiar documento:`, error);
            }
          } 
          // Verificar parámetros específicos para acción no ejecutable (val === 3)
          else if (
            data.accion === 243 &&
            data.act === "recOK" &&
            data.kind === action &&
            data.outid === curtain.id &&
            data.val === 3
          ) {
            console.log(`[CurtainMonitor] Acción no ejecutable detectada para cortina ${curtain.name}`);
            handleOperationCompletion(curtain, action, "already_in_position");
            
            // Limpiar el documento después de procesar
            try {
              const cleanData = {
                ...data,
                accion: 0,
                act: "-"
              };
              await docRef.update(cleanData);
            } catch (error) {
              console.error(`[CurtainMonitor] Error al limpiar documento:`, error);
            }
          }
        }
      }
    });

    unsubscribeRefs.current[`firebase_${curtain.id}`] = unsubscribe;
  }, [canIdIrrigation, currentMac, usuario, handleOperationCompletion]);

  // Función para iniciar el monitoreo de una cortina
  const startMonitoring = useCallback((curtain, action) => {
    console.log(`[CurtainMonitor] Iniciando monitoreo para cortina ${curtain.name}, acción: ${action === 0 ? 'Abrir' : 'Cerrar'}`);
    
    monitorFirebaseConfirmations(curtain, action);
  }, [monitorFirebaseConfirmations]);

  // Función para detener el monitoreo de una cortina
  const stopMonitoring = useCallback((curtainId) => {
    console.log(`[CurtainMonitor] Deteniendo monitoreo para cortina ${curtainId}`);
    
    // Limpiar listener
    if (unsubscribeRefs.current[`firebase_${curtainId}`]) {
      unsubscribeRefs.current[`firebase_${curtainId}`]();
      delete unsubscribeRefs.current[`firebase_${curtainId}`];
    }
  }, []);

  // Función pública para agregar una cortina al monitoreo
  const addCurtainToMonitoring = useCallback((curtain, action) => {
    console.log(`[CurtainMonitor] Agregando cortina al monitoreo:`, curtain.name);

    // Verificar si ya está siendo monitoreada
    const isAlreadyMonitored = monitoredCurtains.some(c => c.id === curtain.id);
    if (isAlreadyMonitored) {
      console.log(`[CurtainMonitor] Cortina ${curtain.name} ya está siendo monitoreada`);
      return;
    }

    // Agregar a la lista de monitoreadas
    setMonitoredCurtains(prev => [...prev, { ...curtain, monitoredAction: action }]);

    // Iniciar monitoreo
    startMonitoring(curtain, action);
  }, [monitoredCurtains, startMonitoring]);

  // Función pública para remover una cortina del monitoreo
  const removeCurtainFromMonitoring = useCallback((curtainId) => {
    console.log(`[CurtainMonitor] Removiendo cortina del monitoreo:`, curtainId);

    stopMonitoring(curtainId);
    setMonitoredCurtains(prev => prev.filter(c => c.id !== curtainId));
  }, [stopMonitoring]);

  // Exponer funciones públicas a través de useImperativeHandle
  useImperativeHandle(ref, () => ({
    addCurtainToMonitoring,
    removeCurtainFromMonitoring,
    getMonitoredCurtains: () => monitoredCurtains
  }), [monitoredCurtains, addCurtainToMonitoring, removeCurtainFromMonitoring]);

  // Listener para eventos de inicio de operación desde ManualCurtainControl
  useEffect(() => {
    const handleCurtainOperationStart = (event) => {
      const { curtainId, action } = event.detail;
      
      if (curtainsData && curtainsData.length > 0) {
        const curtain = curtainsData.find(c => c.id === curtainId);
        if (curtain) {
          addCurtainToMonitoring(curtain, action);
        }
      }
    };

    window.addEventListener('curtainOperationStart', handleCurtainOperationStart);

    return () => {
      window.removeEventListener('curtainOperationStart', handleCurtainOperationStart);
    };
  }, [curtainsData, addCurtainToMonitoring]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      Object.values(unsubscribeRefs.current).forEach(unsubscribe => unsubscribe());
      unsubscribeRefs.current = {};
    };
  }, []);

  // Log de estado actual para debugging
  useEffect(() => {
    if (monitoredCurtains.length > 0) {
      console.log(`[CurtainMonitor] Cortinas actualmente monitoreadas:`,
        monitoredCurtains.map(c => ({ id: c.id, name: c.name, action: c.monitoredAction }))
      );
    }
  }, [monitoredCurtains]);

  // Este componente no renderiza nada visible
  return null;
});

export default CurtainMonitor;

